import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, Trophy } from 'lucide-react';

const PURPLE_SOFT = 'rgba(139, 92, 246, 0.14)';
const PURPLE_BORDER = 'rgba(139, 92, 246, 0.28)';
const PURPLE_GLOW = '0 0 0 1px rgba(139, 92, 246, 0.12), 0 18px 48px rgba(109, 40, 217, 0.18)';

function pickRewardLine(lesson) {
  if (!lesson || typeof lesson !== 'object') return '';
  const obj = typeof lesson.learningObjective === 'string' ? lesson.learningObjective.trim() : '';
  if (obj) return `Great job! ${obj}`;
  const first = Array.isArray(lesson.learningObjectives) ? lesson.learningObjectives[0] : '';
  if (typeof first === 'string' && first.trim()) return `Great job learning: ${first.trim()}`;
  return '';
}

const primaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  minHeight: 56,
  padding: '14px 20px',
  borderRadius: 16,
  border: 'none',
  fontWeight: 800,
  fontSize: 17,
  cursor: 'pointer',
  textDecoration: 'none',
  boxSizing: 'border-box',
  background: 'linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%)',
  color: '#fff',
  boxShadow: '0 4px 16px rgba(109, 40, 217, 0.35)',
};

const secondaryBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: 52,
  padding: '12px 20px',
  borderRadius: 16,
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  border: `2px solid ${PURPLE_BORDER}`,
  background: 'var(--color-surface, #fff)',
  color: 'var(--color-primary, #6d28d9)',
};

const ghostLinkStyle = {
  display: 'block',
  textAlign: 'center',
  marginTop: 14,
  fontWeight: 600,
  fontSize: 15,
  color: 'var(--color-primary, #6d28d9)',
  textDecoration: 'none',
};

/**
 * Kid-friendly lesson completion / celebration (guided lessons).
 * @param {{
 *   lesson: { title?: string, learningObjective?: string, learningObjectives?: string[] },
 *   worldTitle: string,
 *   backToWorld: string,
 *   nextLessonPath: string | null,
 *   nextLessonTitle?: string | null,
 *   onPlayAgain: () => void,
 * }} props
 */
export default function LessonCompletionScreen({
  lesson,
  worldTitle,
  backToWorld,
  nextLessonPath,
  nextLessonTitle,
  onPlayAgain,
}) {
  const lessonTitle = typeof lesson?.title === 'string' && lesson.title.trim() ? lesson.title.trim() : 'this lesson';
  const rewardLine = useMemo(() => pickRewardLine(lesson), [lesson]);
  const showNext = Boolean(nextLessonPath);
  const primaryLabel = showNext ? 'Next lesson →' : `Back to ${worldTitle}`;

  return (
    <div
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        padding: 'max(16px, env(safe-area-inset-top)) 18px calc(28px + env(safe-area-inset-bottom, 0px))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(165deg, rgba(139, 92, 246, 0.16) 0%, rgba(167, 139, 250, 0.06) 28%, var(--color-bg) 52%, var(--color-bg) 100%)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 24,
          padding: '28px 22px 26px',
          boxSizing: 'border-box',
          background: 'var(--color-surface, var(--color-bg-card))',
          border: `1px solid ${PURPLE_BORDER}`,
          boxShadow: PURPLE_GLOW,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            top: 0,
            height: 140,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                left: `${6 + (i * 10) % 88}%`,
                top: -6,
                width: i % 3 === 0 ? 10 : 7,
                height: i % 3 === 0 ? 14 : 10,
                borderRadius: 2,
                background: ['#a78bfa', '#f472b6', '#34d399', '#fbbf24', '#60a5fa'][i % 5],
              }}
              initial={{ y: 0, opacity: 0.95, rotate: 0 }}
              animate={{ y: 200, opacity: 0, rotate: 200 + i * 20 }}
              transition={{ duration: 1.85, delay: 0.04 * i, ease: [0.22, 1, 0.36, 1] }}
            />
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1 }} role="status" aria-live="polite">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.08 }}
            style={{
              width: 96,
              height: 96,
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: `linear-gradient(145deg, ${PURPLE_SOFT}, rgba(250, 245, 255, 0.9))`,
              border: `2px solid ${PURPLE_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              boxShadow: 'inset 0 2px 12px rgba(255,255,255,0.65)',
            }}
          >
            <Trophy size={44} strokeWidth={1.75} style={{ color: '#d97706' }} aria-hidden />
            <CheckCircle2
              size={28}
              strokeWidth={2.5}
              style={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                color: '#16a34a',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))',
              }}
              aria-hidden
            />
            <Sparkles
              size={22}
              style={{ position: 'absolute', top: 6, right: 10, color: '#a78bfa' }}
              aria-hidden
            />
          </motion.div>

          <h1
            style={{
              fontSize: 'clamp(1.55rem, 5vw, 1.85rem)',
              fontWeight: 800,
              margin: '0 0 10px',
              lineHeight: 1.2,
              color: 'var(--color-text)',
            }}
          >
            Lesson complete!
          </h1>
          <p
            style={{
              margin: '0 0 12px',
              fontSize: '1.05rem',
              lineHeight: 1.45,
              color: 'var(--color-text)',
              opacity: 0.88,
            }}
          >
            You finished <strong>{lessonTitle}</strong>.
          </p>
          {rewardLine ? (
            <p
              style={{
                margin: '0 0 22px',
                fontSize: 15,
                lineHeight: 1.5,
                color: 'var(--color-primary, #6d28d9)',
                fontWeight: 600,
                opacity: 0.92,
              }}
            >
              {rewardLine}
            </p>
          ) : (
            <div style={{ height: 8 }} />
          )}

          {showNext && nextLessonTitle ? (
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--color-text-soft, var(--color-text))',
                opacity: 0.85,
              }}
            >
              Up next: {nextLessonTitle}
            </p>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {showNext ? (
              <Link to={nextLessonPath} style={primaryBtnStyle} replace>
                {primaryLabel}
              </Link>
            ) : (
              <Link to={backToWorld} style={primaryBtnStyle} replace>
                {primaryLabel}
              </Link>
            )}
            <button type="button" style={secondaryBtnStyle} onClick={onPlayAgain}>
              Play again
            </button>
            {showNext ? (
              <Link to={backToWorld} style={ghostLinkStyle}>
                Back to {worldTitle}
              </Link>
            ) : null}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
